import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Tooltip from 'react/components/common/Tooltip/Tooltip';
import Spinner from 'react/components/common/Spinner/Spinner';
import GaugeBar from './GaugeBar/GaugeBar';
import './TopicSelectorWidget.scss';

const TopicSelector = ({ topics, onSelect, selectedId, loading }) => {
  const elementsContainerRef = useRef(null);

  // Process topics data - add "All Interests" if not present and add image paths
  const data = useMemo(() => {
    if (!topics || !topics.length) return [];

    const processedTopics = [...topics];

    // Add "All Interests" at the beginning if not present
    if (processedTopics[0]?.id !== 'All Interests') {
      processedTopics.unshift({ id: 'All Interests' });
    }

    // Add title and image to each topic
    return processedTopics.map((topic) => ({
      ...topic,
      title: topic.id,
      image: `images/images/topics/${topic.id.trim().toLowerCase().split(' ').join('_')}.png`,
    }));
  }, [topics]);

  // Auto-select first element when data loads if no selectedId
  useEffect(() => {
    if (data && data.length > 0 && !selectedId) {
      if (onSelect) {
        onSelect(data[0]);
      }
    }
  }, [data, selectedId, onSelect]);

  const selectElement = (element) => {
    if (onSelect) {
      onSelect(element);
    }
  };

  const isSelected = (element) => element.id === (selectedId || data[0]?.id);

  const getElementId = (id) => (id ? `element-id-${id.replace(/\s/g, '')}` : '');

  const handleScrollLeft = () => {
    if (elementsContainerRef.current) {
      // Calculate scroll amount based on container width to scroll one "page" of elements
      const containerWidth = elementsContainerRef.current.offsetWidth;
      elementsContainerRef.current.scrollBy({ left: -containerWidth, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (elementsContainerRef.current) {
      // Calculate scroll amount based on container width to scroll one "page" of elements
      const containerWidth = elementsContainerRef.current.offsetWidth;
      elementsContainerRef.current.scrollBy({ left: containerWidth, behavior: 'smooth' });
    }
  };

  const isLoading = loading || !data || data.length === 0;

  if (isLoading) {
    return (
      <div className="element-selector-drv element-selector-loading">
        <Spinner show />
      </div>
    );
  }

  return (
    <div className="element-selector-drv">
      <div className="navigate-icon-wrapper left" onClick={handleScrollLeft}>
        <i className="navigate-icon icon-chevron-left prevPage"></i>
      </div>

      <div className="elements-container" ref={elementsContainerRef}>
        <div className="scroll-container">
          {data.map((element) => (
            <div
              key={element.id}
              id={getElementId(element.id)}
              className="element"
              onClick={() => selectElement(element)}
            >
              <Tooltip
                content={element.tooltip}
                options={{
                  allowHTML: true,
                  maxWidth: 'max-content',
                  popperOptions: { modifiers: { flip: { enabled: false } } },
                }}
              >
                <div className={classNames('element-container', { selected: isSelected(element) })}>
                  <div className="image">
                    <div className="img-dark-overlay"></div>
                    <img src={element.image} width="204" height="130" alt={element.title} />
                    <span>{element.title}</span>
                  </div>

                  {element.title !== 'All Interests' && (
                    <div className="values-container">
                      <div className="ts-bar">
                        <GaugeBar percents={element.percents} options={{ resize: true }} />
                      </div>

                      <div className="values">
                        <span>{element.percentValue}% | </span>
                        <span className="score">{element.value}</span>
                      </div>
                    </div>
                  )}
                </div>
              </Tooltip>

              {isSelected(element) && <div className="triangle-down" />}
            </div>
          ))}
        </div>
      </div>

      <div className="navigate-icon-wrapper right" onClick={handleScrollRight}>
        <i className="navigate-icon icon-chevron-right nextPage"></i>
      </div>
    </div>
  );
};

TopicSelector.propTypes = {
  topics: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      percents: PropTypes.number,
      percentValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      tooltip: PropTypes.string,
    })
  ),
  onSelect: PropTypes.func,
  selectedId: PropTypes.string,
  loading: PropTypes.bool,
};

TopicSelector.defaultProps = {
  topics: [],
  onSelect: null,
  selectedId: null,
  loading: false,
};

export default TopicSelector;
